import * as z from 'zod';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Filter } from '@ais-ui/websockets';
import { SelectType } from '@ais-ui/ui-core';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { timeFormatHoursMinutesSecondsSchema, checkDirectionAngleSchema } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { mutualDetectorFormSchema } from '../Mutations/detectorForm';


export const useCableTriggers = (): SelectType[] => {
  const { messages } = useLocalization();

  return [
    {
      value: 'detectNavStatus',
      label: 'Navigational status',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipNavigationalStatus,
    },
    {
      value: 'detectDrifting',
      label: 'Drifting',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipDrifting,
    },
    {
      value: 'detectInvalidAis',
      label: 'Invalid AIS data',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipInvalidAISData,
    },
    {
      value: 'doExitCheck',
      label: 'Exit check',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipExitCheck,
    },
    {
      value: 'detectSpeedDrop',
      label: 'Speed drop',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipSpeedDrop,
    },
    {
      value: 'detectAnchoredDrifting',
      label: 'Anchored, drifting into region',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipAnchoredDriftingIntoRegion,
    },
    {
      value: 'detectAnchoredDriftingOut',
      label: 'Anchored, drifting out of region',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipAnchoredDriftingOutOfRegion,
    },
    {
      value: 'detectAnchoredWithin',
      label: 'Anchor dropped inside region',
      help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipAnchoredDriftingDroppedInsideRegion,
    },
  ];
};

export const fieldToCableAccordionMap: {
  [key in keyof CableDetectorFormValues]: string;
} = {
  entireVesselInside: 'advanced-item',
  name: 'detector-item',
  enabled: 'detector-item',
  selectedGroups: 'detector-item',
  severity: 'detector-item',
  filterId: 'detector-item',
  maintainShiplist: 'detector-item',
  acknowledgeRequired: 'detector-item',
  timeBeforeActivation: 'advanced-item',
  timeBeforeDeactivation: 'advanced-item',
  timeBetweenTrigger: 'advanced-item',
  timeoutWhenActive: 'advanced-item',
  sendShipNotification: 'advanced-item',
  shipNotificationText: 'advanced-item',
  useVesselDimensions: 'advanced-item',
  navigationStatusTriggerThreshold: 'advanced-item',
  navigationStatusIgnoreThreshold: 'advanced-item',
  driftingSpeedOverGroundThreshold: 'advanced-item',
  driftingAngleThreshold: 'advanced-item',
  driftingTimeThreshold: 'advanced-item',
  exitCheckThreshold: 'advanced-item',
  exitCheckTriggerLost: 'advanced-item',
  deltaSpeedOverGroundThreshold: 'advanced-item',
  emails: 'detector-item',
  phones: 'detector-item',
  cableTriggers: 'cable-item',
};

export const cableDetectorFormSchema = mutualDetectorFormSchema
  .extend({
    activationText: z.string().optional(),
    deactivationText: z.string().optional(),
    directionMinAngle: checkDirectionAngleSchema.optional(),
    directionMaxAngle: checkDirectionAngleSchema.optional(),
    timeBeforeActivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBeforeDeactivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBetweenTrigger: timeFormatHoursMinutesSecondsSchema.optional(),
    timeoutWhenActive: timeFormatHoursMinutesSecondsSchema.optional(),
    severity: z.string(),
    sendShipNotification: z.boolean(),
    shipNotificationText: z.string().optional(),
    cableTriggers: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    ),
    navigationStatusTriggerThreshold: z.number().optional(),
    navigationStatusIgnoreThreshold: z.number().optional(),
    driftingSpeedOverGroundThreshold: z.number().optional(),
    driftingAngleThreshold: z.number().optional(),
    driftingTimeThreshold: z.number().optional(),
    exitCheckThreshold: z.number().optional(),
    exitCheckTriggerLost: z.boolean().optional(),
    deltaSpeedOverGroundThreshold: z.number().optional(),
    useVesselDimensions: z.boolean(),
    entireVesselInside: z.boolean(),
  })
  .refine((data) => Boolean(data.shapeId) || Boolean(data.shape), {
    message: 'Shape must be provided',
    path: ['shapeId', 'shape'],
  });
export type CableDetectorFormValues = z.infer<typeof cableDetectorFormSchema>;

type CableTriggerBooleans = {
  [key: string]: boolean;
};
const convertCableTriggersToBackend = (cableTriggers: SelectType[]) => {
  const allKeys = [
    'detectNavStatus',
    'detectDrifting',
    'detectInvalidAis',
    'doExitCheck',
    'detectSpeedDrop',
    'detectAnchoredDrifting',
    'detectAnchoredWithin',
    'detectAnchoredDriftingOut',
  ];

  const presentKeys = new Set(cableTriggers.map((trigger) => trigger.value));

  // Create the final object with all keys, setting to false if not present in the original list
  const cableTriggerBooleans: CableTriggerBooleans = allKeys.reduce(
    (acc: CableTriggerBooleans, key) => {
      acc[key] = presentKeys.has(key);
      return acc;
    },
    {}
  );

  return cableTriggerBooleans;
};

export const convertCableTriggersToFrontend = (
  cableTriggerBooleans: CableTriggerBooleans,
  cableTriggers: SelectType[]
): SelectType[] => {
  return Object.entries(cableTriggerBooleans)
    .filter(([key, value]) => value)
    .map(([key]) => {
      const trigger = cableTriggers.find((trigger) => trigger.value === key);
      if (!trigger) {
        throw new Error(`Label for ${key} not found`);
      }
      return trigger;
    });
};

interface CableObjectValues {
  navStatusTriggerThreshold?: number;
  navStatusIgnoreThreshold?: number;
  driftingSogThreshold?: number;
  driftingChdThreshold?: number;
  driftingTimeThreshold?: number;
  exitCheckThreshold?: number;
  exitCheckTriggerLost?: boolean;
  deltaSogThreshold?: number;
  advancedCableConfig?: boolean;
}

export const convertFormValuesToBackend = (
  detectorType: string,
  data: CableDetectorFormValues,
  shape: GeoJson,
  filter: Filter | undefined
): EventdetectorDefinition => {
  let cableTriggers = {};
  const cableObjectValues: CableObjectValues = {};
  if (detectorType === 'cable') {
    cableTriggers = convertCableTriggersToBackend(data.cableTriggers);
    cableObjectValues['navStatusTriggerThreshold'] =
      data.navigationStatusTriggerThreshold;
    cableObjectValues['navStatusIgnoreThreshold'] =
      data.navigationStatusIgnoreThreshold;
    cableObjectValues['driftingSogThreshold'] =
      data.driftingSpeedOverGroundThreshold;
    cableObjectValues['driftingChdThreshold'] = data.driftingAngleThreshold;
    cableObjectValues['driftingTimeThreshold'] = data.driftingTimeThreshold;
    cableObjectValues['exitCheckThreshold'] = data.exitCheckThreshold;
    cableObjectValues['exitCheckTriggerLost'] = data.exitCheckTriggerLost;
    cableObjectValues['deltaSogThreshold'] = data.deltaSpeedOverGroundThreshold;
    cableObjectValues['advancedCableConfig'] = true;
  }

  let checkDirection = null;
  if (data.directionMinAngle && data.directionMaxAngle) {
    checkDirection = {
      minAngle: data.directionMinAngle,
      maxAngle: data.directionMaxAngle,
    };
  }

  const notifications = [];
  for (const email of data.emails) {
    notifications.push({
      who: email,
      action: 'email',
    });
  }

  for (const phone of data.phones) {
    notifications.push({
      who: phone,
      action: 'sms',
    });
  }

  return {
    name: data.name,
    type: detectorType,
    acl: data.selectedGroups.map((group) => group.value),
    id: null,
    enabled: data.enabled,
    advancedConfiguration: true,
    checkDirection: checkDirection,
    useVesselDimensions: data.useVesselDimensions,
    entireVessel: data.entireVesselInside,
    sendShipNotification: data.sendShipNotification,
    shipNotificationText: data.shipNotificationText,
    timeBeforeActivation: data.timeBeforeActivation
      ? data.timeBeforeActivation
      : '',
    timeBeforeDeactivation: data.timeBeforeDeactivation
      ? data.timeBeforeDeactivation
      : '',
    activationText: data.activationText ? data.activationText : '',
    deactivationText: data.deactivationText ? data.deactivationText : '',
    timeBetweenTriggers: data.timeBetweenTrigger ? data.timeBetweenTrigger : '',
    timeoutWhenActive: data.timeoutWhenActive ? data.timeoutWhenActive : '',
    severity: data.severity,
    shape: data.shapeId,
    geometry: shape,
    filterId: filter?.uuid ? filter.uuid : null,
    filterString: filter?.filter ? filter?.filter : '',
    notify: notifications,
    notifyOnActivation: true,
    notifyOnDeactivation: true,
    ackRequired: data.acknowledgeRequired,
    shiplist: {
      active: data.maintainShiplist,
    },
    ...cableTriggers,
    ...cableObjectValues,
  };
};
